import {
  Device,
  Document,
  RoadmapCards,
  roadmapCardsSchema,
  RoadmapStages,
  roadmapStagesSchema,
  RoadmapTask,
  RoadmapTasks,
  taskDefinitionsSchema,
} from "@models";
import { meetsVisibilityConditions } from "../utils/roadmap-visibilty-conditions";
import { forEach } from "lodash";
import { makeAutoObservable } from "mobx";
import {
  RoadmapAsArray,
  RoadmapAsMap,
  RoadmapCardWithTaskMap,
  RoadmapConfig,
} from "../config";
import {
  getRoadmapCards,
  getRoadmapStages,
  getRoadmapTasks,
} from "../services";
import { sortByOrder } from "./helpers/sortByOrder";

export class DeviceStore {
  // Todo
  _roadmaps: Map<string, RoadmapAsMap> = new Map();
  _activeGlobalDeviceIds: Map<string, string> = new Map();
  _globalDeviceSubscribers: Set<() => void> = new Set();

  rootStore: unknown;

  constructor(rootStore: unknown) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
    this.rootStore = rootStore;
  }

  getActiveGlobalDeviceId(orgId: string): string | null {
    return this._activeGlobalDeviceIds.get(orgId) ?? null;
  }

  setActiveGlobalDeviceId(orgId: string, deviceId: string | null) {
    if (!deviceId) {
      this._activeGlobalDeviceIds.delete(orgId);
      this.notifyGlobalDeviceSubscribers();
      return;
    }

    this._activeGlobalDeviceIds.set(orgId, deviceId);
    this.notifyGlobalDeviceSubscribers();
  }

  clearActiveGlobalDeviceId(orgId: string) {
    this._activeGlobalDeviceIds.delete(orgId);
    this.notifyGlobalDeviceSubscribers();
  }

  hasActiveGlobalDevice(orgId: string): boolean {
    return this._activeGlobalDeviceIds.has(orgId);
  }

  subscribeToGlobalDeviceContext(listener: () => void) {
    this._globalDeviceSubscribers.add(listener);

    return () => {
      this._globalDeviceSubscribers.delete(listener);
    };
  }

  private notifyGlobalDeviceSubscribers() {
    this._globalDeviceSubscribers.forEach((listener) => listener());
  }

  async fetchRoadmapTasks(
    orgId: string,
    deviceId: string
  ): Promise<RoadmapTasks> {
    const { data, status } = await getRoadmapTasks(orgId, deviceId);
    if (status === 404) {
      return [];
    }

    return await taskDefinitionsSchema.validate(data);
  }

  async fetchRoadmapCards(
    orgId: string,
    deviceId: string
  ): Promise<RoadmapCards> {
    const { data, status } = await getRoadmapCards(orgId, deviceId);
    if (status === 404) {
      return [];
    }

    return await roadmapCardsSchema.validate(data);
  }

  async fetchRoadmapStages(
    orgId: string,
    deviceId: string
  ): Promise<RoadmapStages> {
    const { data, status } = await getRoadmapStages(orgId, deviceId);
    if (status === 404) {
      return [];
    }

    return await roadmapStagesSchema.validate(data);
  }

  mapRoadmap(
    tasks: RoadmapTasks,
    device: Device,
    documents: Document[],
    cards: RoadmapCards = [],
    stages: RoadmapStages = []
  ) {
    const roadmap: RoadmapAsMap = new Map();

    const roadmapConfig = this.mapDeviceConfigurationToRoadmap(
      device,
      documents,
      sortByOrder(tasks, "taskKey"),
      sortByOrder(cards, "cardKey"),
      sortByOrder(stages, "stageKey")
    );

    forEach(roadmapConfig, (phase) => {
      let cardMap: Map<string, RoadmapCardWithTaskMap> = new Map();
      forEach(phase.cards, (card) => {
        let taskMap: Map<string, RoadmapTask> = new Map();
        forEach(card.tasks, (task) => {
          taskMap.set(task.taskKey, {
            ...task,
          });
        });
        let newCard = { ...card, tasks: taskMap };
        cardMap.set(card.cardKey, newCard);
      });
      let newPhase = {
        ...phase,
        cards: cardMap,
      };
      roadmap.set(phase.stageKey, newPhase);
    });

    return Array.from(roadmap.values()).map((phase) => ({
      ...phase,
      cards: Array.from(phase.cards.values()).map((card) => ({
        ...card,
        tasks: Array.from(card.tasks.values()),
      })),
    }));
  }

  getRoadmap = (deviceId: string): RoadmapAsArray | undefined => {
    const roadmap = this._roadmaps.get(deviceId);
    if (!roadmap) {
      return undefined;
    }

    return Array.from(roadmap.values()).map((phase) => ({
      ...phase,
      cards: Array.from(phase.cards.values()).map((card) => ({
        ...card,
        tasks: Array.from(card.tasks.values()),
      })),
    }));
  };

  replaceArrayObjectByKey(obj: any, key = "id", files = []) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        if (Array.isArray(obj[prop])) {
          for (var i = 0; i < obj[prop].length; i++) {
            if (
              obj[prop][i].hasOwnProperty(key) &&
              !obj[prop][i].hasOwnProperty("files") &&
              !obj[prop][i].hasOwnProperty("address") &&
              !obj[prop][i].hasOwnProperty("model")
            ) {
              // @ts-ignore
              files.push(obj[prop][i]);
              obj[prop][i] = obj[prop][i][key];
            } else {
              this.replaceArrayObjectByKey(obj[prop][i], key, files);
            }
          }
        } else if (typeof obj[prop] === "object") {
          this.replaceArrayObjectByKey(obj[prop], key, files);
        }
      }
    }
    return { obj, files };
  }

  mapDeviceConfigurationToRoadmap = (
    device: Device,
    documents: Document[],
    tasks: RoadmapTasks,
    cards: RoadmapCards = [],
    stages: RoadmapStages = []
  ): RoadmapConfig => {
    return stages.map((stage) => ({
      ...stage,
      cards: cards
        .filter((card) => {
          let visible = card.parentStageKey === stage.stageKey;

          if (card.visibilityConditions && visible) {
            visible = meetsVisibilityConditions(
              card.visibilityConditions,
              device,
              documents
            );
          }

          return visible;
        })
        .map((card) => ({
          ...card,
          tasks: tasks?.filter((task) => task.parentCardKey === card.cardKey),
        }))
        .sort((a, b) => {
          a.order = a.order ?? 10;
          b.order = b.order ?? 10;

          return a.order - b.order;
        }),
    }));
  };
}
